import {
  borderWidth,
  color,
  radius,
  size,
  spacing,
  StyleVars,
  TRANSPARENT,
  TypographyName,
} from "./types";

/* --------------------------------------------------------------------- Tabs */

export type TabsSize = "small" | "large";
export type TabsVariant = "primary" | "secondary";

export const tabsSpec = {
  defaults: { size: "small" as TabsSize, variant: "primary" as TabsVariant },
  sizes: {
    small: {
      vars: {
        "--tab-height": size("control-sm"),
        "--tab-padding-x": spacing("3"),
        "--tabs-padding": spacing("1"),
      } satisfies StyleVars,
      typography: "caption-13-medium" as TypographyName,
    },
    large: {
      vars: {
        "--tab-height": "42px",
        "--tab-padding-x": spacing("4"),
        "--tabs-padding": spacing("1"),
      } satisfies StyleVars,
      typography: "text-14-medium" as TypographyName,
    },
  },
  variants: {
    primary: {
      "--tabs-bg": color("interaction-grey-light"),
      "--tabs-radius": radius("lg"),
      "--tab-radius": radius("md"),
      "--tab-bg": TRANSPARENT,
      "--tab-bg-hover": color("interaction-grey-light-hv"),
      "--tab-bg-active": color("background-white"),
      "--tab-fg": color("text-secondary-v2"),
      "--tab-fg-active": color("text-primary"),
      "--tab-underline": TRANSPARENT,
    },
    secondary: {
      "--tabs-bg": TRANSPARENT,
      "--tabs-radius": radius("none"),
      "--tab-radius": radius("none"),
      "--tab-bg": TRANSPARENT,
      "--tab-bg-hover": TRANSPARENT,
      "--tab-bg-active": TRANSPARENT,
      "--tab-fg": color("text-secondary-v2"),
      "--tab-fg-active": color("interaction-violet"),
      "--tab-underline": color("interaction-violet"),
    },
  } satisfies Record<TabsVariant, StyleVars>,
  base: {
    "--tabs-gap": spacing("1"),
    "--tab-underline-width": borderWidth("sm"),
  } satisfies StyleVars,
};

/* -------------------------------------------------------- Segmented control */

export const segmentedControlSpec = {
  base: {
    "--segmented-bg": color("interaction-grey-light"),
    "--segmented-radius": radius("lg"),
    "--segmented-padding": spacing("1"),
    "--segment-height": size("control-sm"),
    "--segment-padding-x": spacing("3"),
    "--segment-radius": radius("md"),
    "--segment-bg-active": color("background-white"),
    "--segment-fg": color("text-secondary-v2"),
    "--segment-fg-active": color("text-primary"),
    "--segment-shadow-active": "var(--shadow-soft)",
    "--segment-icon-size": size("icon-sm"),
  } satisfies StyleVars,
  typography: "caption-12-medium" as TypographyName,
};

/* ------------------------------------------------------------------- Header */

export const headerSpec = {
  base: {
    "--header-height": size("topbar"),
    "--header-bg": color("background-white"),
    "--header-border": color("border-secondary"),
    "--header-border-width": borderWidth("hairline"),
    "--header-padding-x": spacing("6"),
    "--header-gap": spacing("3"),
    "--header-logo-height": size("icon-lg"),
    "--header-divider-height": spacing("4"),
    "--header-tab-radius": radius("lg"),
    "--header-tab-padding-x": spacing("3"),
    "--header-tab-padding-y": spacing("1-5"),
    "--header-tab-fg": color("text-secondary-v2"),
    "--header-tab-fg-active": color("text-primary"),
    "--header-tab-bg-hover": color("background-gray-medium"),
    "--header-tab-bg-active": color("background-gray-medium"),
  } satisfies StyleVars,
  titleTypography: "text-14-semibold" as TypographyName,
  captionTypography: "caption-11-medium" as TypographyName,
  tabTypography: "text-14-medium" as TypographyName,
};

/* ------------------------------------------------------------------ Sidebar */

export const sidebarSpec = {
  base: {
    "--sidebar-width": size("sidebar"),
    // Сайдбар чуть светлее полотна страницы, но темнее карточек — как в макете.
    "--sidebar-bg": color("background-content-tertiary"),
    "--sidebar-border": color("border-secondary"),
    "--sidebar-border-width": borderWidth("hairline"),
    "--sidebar-padding-x": spacing("3"),
    "--sidebar-padding-y": spacing("6"),
    "--sidebar-section-gap": spacing("6"),
    "--sidebar-label-color": color("text-secondary-v2"),
    "--sidebar-label-spacing": "var(--font-letter-spacing-wide)",
    "--nav-item-height": size("control-md"),
    "--nav-item-padding-x": spacing("2"),
    "--nav-item-radius": radius("lg"),
    "--nav-item-gap": spacing("2-5"),
    "--nav-item-icon-size": size("icon-sm"),
    "--nav-item-fg": color("text-secondary-v2"),
    "--nav-item-bg-hover": color("interaction-grey-light"),
    "--nav-item-fg-hover": color("text-primary"),
    // Дизайн-ревью: выделение делаем спокойнее — серый фон вместо фиолетового.
    "--nav-item-bg-active": color("interaction-grey-light"),
    "--nav-item-fg-active": color("text-primary"),
  } satisfies StyleVars,
  itemTypography: "text-14-medium" as TypographyName,
  labelTypography: "caption-11-medium" as TypographyName,
};

/* -------------------------------------------------------------------- Modal */

export const modalSpec = {
  base: {
    "--modal-overlay": color("background-overlay"),
    "--modal-bg": color("background-white"),
    "--modal-radius": radius("3xl"),
    "--modal-padding": spacing("8"),
    "--modal-width": "460px",
    "--modal-gap": spacing("4"),
    "--modal-shadow": "var(--shadow-float)",
    "--modal-actions-gap": spacing("2-5"),
  } satisfies StyleVars,
  titleTypography: "header-20-semibold" as TypographyName,
};

/* ---------------------------------------------------------------- App shell */

export const shellSpec = {
  base: {
    // Хедер задаёт --header-height инлайном на себе, а шелл — его сосед, поэтому
    // высоту приходится объявлять и здесь: от неё зависят липкий сайдбар и его
    // собственный скролл. Значение одно и то же — токен size/topbar.
    "--header-height": size("topbar"),
    "--shell-main-padding-x": spacing("10"),
    "--shell-main-padding-y": spacing("8"),
    // max-w-5xl из макета: витрина и документация делят одну колонку контента.
    "--shell-content-width": "1024px",
    "--shell-gap": spacing("6"),
    "--shell-bg": color("background-gray-light"),
  } satisfies StyleVars,
};
